import React, { useCallback } from "react";
import * as R from "ramda";

import {
  ADDRESS,
  CAD,
  CHUTE,
  TYPE,
  WINDOW,
  PRIO,
  PARCEL_NUMBER,
  KING,
  LOAD,
  KG,
  REQ,
} from "../../../constants/strings";
import { COLUMN_ACCESSORS as CA } from "../../../constants/table";
import BodyCell from "../../common/Table/BodyCell";
import HeaderCell from "../../common/Table/HeaderCell";
import { Add, RadioIcon, Remove, SpotIcon, Defer } from "../../icons";
import palette from "../../styles/palette";
import { HighValueItem, ServiceItem } from "../../common/Table/Items";
import {
  gramsToKg,
  isRemoveRequest,
  isDeferredRequest,
  isRequestHasResponse,
  getCadScannedStatusColor,
  getCadStatusTextColor,
} from "../../../features/routeDetail/model";
import { UNIVERSE_TRACKING_LINK_BASE } from "../../../constants/app";
import { getParcelRequestString } from "../../../models/routeDetail";

const parcelDetailsColumns = ({
  columnsConfigs,
  headerCellClasses,
  bodyCellClasses,
  modifiers: {
    onUpdateKingParcel = R.identity,
    onParcelRequestClick = R.identity,
    onParcelNumberClick = R.identity,
  } = {},
  isKingParcelColumnDisabled,
}) => [
  {
    Cell: ({ rowData }) => {
      return (
        <HighValueItem
          rowData={rowData}
          classes={bodyCellClasses}
          // testId={`${CA.SERVICE}-icon-${getStatusTextColor(value)}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.HIGH_VALUE],
    accessor: CA.HIGH_VALUE,
  },
  {
    Cell: ({ value }) => (
      <BodyCell
        value={<ServiceItem value={value} />}
        classes={bodyCellClasses}
      />
    ),
    columnConfig: columnsConfigs[CA.SERVICE],
    accessor: CA.SERVICE,
  },
  {
    Header: () => <HeaderCell header={PRIO} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.PRIORITY],
    accessor: CA.PRIORITY,
  },
  {
    Header: () => (
      <HeaderCell header={PARCEL_NUMBER} classes={headerCellClasses} />
    ),
    Cell: ({ value, rowData }) => {
      const trackLink = (
        <a
          className={bodyCellClasses.link}
          target="_blank"
          rel="noopener noreferrer"
          href={`${UNIVERSE_TRACKING_LINK_BASE}${rowData.parcelCode}`}
        >
          {value}
        </a>
      );
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();
          onParcelNumberClick(value);
        },
        [value]
      );
      return (
        <BodyCell
          value={trackLink}
          classes={bodyCellClasses}
          onClick={onClick}
        />
      );
    },
    columnConfig: columnsConfigs[CA.PARCEL_NUMBER],
    accessor: CA.PARCEL_NUMBER,
  },
  {
    Header: () => <HeaderCell header={ADDRESS} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellCroppedText]}
      />
    ),
    columnConfig: columnsConfigs[CA.ADDRESS],
    accessor: CA.ADDRESS,
  },
  {
    Header: () => <HeaderCell header={KING} classes={headerCellClasses} />,
    Cell: (row) => {
      const icon = <RadioIcon checked={row.value} />;
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();

          if (row.value) {
            return;
          }

          onUpdateKingParcel(row);
        },
        [row]
      );

      return (
        <BodyCell
          onClick={onClick}
          value={icon}
          classes={bodyCellClasses}
          disabled={isKingParcelColumnDisabled}
          data-testid={`${CA.KING}-icon-${row.value ? "checked" : "unchecked"}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.KING],
    accessor: CA.KING,
  },
  {
    Header: () => <HeaderCell header={CAD} classes={headerCellClasses} />,
    Cell: ({ value, rowData }) => {
      const icon = (
        <SpotIcon color={getCadScannedStatusColor(value, rowData)} />
      );
      return (
        <BodyCell
          value={icon}
          classes={bodyCellClasses}
          data-testid={`${CA.CAD_SCANNED}-icon-${getCadStatusTextColor(
            value,
            rowData
          )}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.CAD_SCANNED],
    accessor: CA.CAD_SCANNED,
  },
  {
    Header: () => <HeaderCell header={LOAD} classes={headerCellClasses} />,
    Cell: ({ value }) => {
      const icon = (
        <SpotIcon
          color={value ? palette.statuses.statusGreen : palette.dpdRed}
        />
      );
      return (
        <BodyCell
          value={icon}
          classes={bodyCellClasses}
          data-testid={`${CA.LOAD}-icon-${value ? "green" : "red"}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.LOAD],
    accessor: CA.LOAD,
  },
  {
    Header: () => <HeaderCell header={WINDOW} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.WINDOW],
    accessor: CA.WINDOW,
  },
  {
    Header: () => <HeaderCell header={TYPE} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellCroppedText]}
      />
    ),
    columnConfig: columnsConfigs[CA.TYPE],
    accessor: CA.TYPE,
  },
  {
    Header: () => <HeaderCell header={KG} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={gramsToKg(value.weight)}
        classes={bodyCellClasses}
        extraCellClasses={[
          value.actualWeight && bodyCellClasses.cellYellowText,
        ]}
      />
    ),
    columnConfig: columnsConfigs[CA.WEIGHT],
    accessor: CA.WEIGHT,
  },
  {
    Header: () => <HeaderCell header={CHUTE} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.CHUTE],
    accessor: CA.CHUTE,
  },
  {
    Header: () => <HeaderCell header={REQ} classes={headerCellClasses} />,
    Cell: ({ value, rowData }) => {
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();
          onParcelRequestClick(rowData);
        },
        [rowData]
      );
      if (!value?.length) {
        return null;
      }

      const request = R.head(value);
      const bodyProps = {};

      if (isDeferredRequest(request)) {
        bodyProps.value = <Defer />;
      } else if (isRequestHasResponse(request)) {
        bodyProps.value = null;
      } else if (isRemoveRequest(request)) {
        bodyProps.value = <Remove />;
      } else {
        bodyProps.value = <Add />;
      }

      return (
        <BodyCell
          classes={bodyCellClasses}
          onClick={onClick}
          {...bodyProps}
          data-testid={`${CA.REQUESTS}-icon-${getParcelRequestString(request)}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.REQUESTS],
    accessor: CA.REQUESTS,
  },
];

export default parcelDetailsColumns;
